import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DropdownMenu, {
  MenuItem
} from 'screens/Applications&WorkOrders/Components/ActionMenu/components/DropdownMenu';
import EditButton from 'screens/Applications&WorkOrders/Components/ActionMenu/components/EditButton';
import SpinnerModal from 'components/SpinnerModal';
import DeleteOutlinedIcon from 'assets/delete_outlined.svg?react';
import DuplicateIcon from 'assets/file_copy_icon.svg?react';
import PDFIcon from 'assets/pdf.svg?react';
import AddIcon from 'assets/add.svg?react';
import { paths } from 'routes/paths';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { Context } from 'components/Store';
import { useAuth } from 'utilities/base-auth';
import usePopulateMapData from 'hooks/usePopulateMapData';
import usePlanServiceApi from 'hooks/usePlanServiceApi';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import catchCancel from 'helpers/catchCancel';
import { FETCH_TYPES } from 'helpers/constants';
import { shouldShowPlanCropSeasonMismatch } from 'helpers/cropSeasonMismatch';
import showCropSeasonMismatchModal from 'helpers/showCropSeasonMismatchModal';
import {
  PLAN_DELETE_V2,
  PLAN_DELETE_V2_BY_ORG
} from 'constants/planServiceV2Flags';
import { OperationType } from 'components/RecordsListView/constants';
import { Flow } from 'screens/Applications&WorkOrders/RouteAdmin';
import { RecordSourceType } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import showConfirmModal from 'components/ConfirmModal';
import { PLAN_TO_OPERATION_MODAL_CONTENT } from 'screens/Plans/constants';
import usePlanEllipsisOptions from 'screens/Plans/hooks/usePlanEllipsisOptions';
import usePlanToOperationAction from 'screens/Plans/hooks/usePlanToOperationAction';
import mapPlanToRec from 'screens/Plans/ConvertPlanToRecModal/helpers/dataHelpers';
import ConvertPlanModalV2 from 'screens/Plans/ConvertPlanModalV2';
import { Plan, SelectedProducts } from 'screens/Plans/ConvertPlanModalV2/types';
import planNotification from 'screens/Plans/redesign/components/Notification';
import './PlanActionMenu.css';

const DELETE_ICON_STYLE = { color: '#D92D20' };
const MENU_ICON_STYLE = { width: '18px', height: '18px' };

export interface PlanActionMenuProps {
  /**
   * The full raw Plan (not just id/name) — `handleEdit`/`handleDuplicate`
   * and `usePlanToOperationAction` read `cropZones[].cropSeasonId` off it to
   * detect a crop-season mismatch before navigating.
   */
  plan: Plan;
  /**
   * Fired after a successful delete so the list can re-select + reload —
   * the inline detail panel stays on the same route, so there's no route
   * change left to trigger a refetch the way the legacy `PlanView` got for free.
   */
  onPlanDeleted?: (planId: string) => void;
}

/**
 * Action menu for the Plan detail panel's header (EditButton + DropdownMenu),
 * mirroring the legacy `PlanView` TopHeader menu and reusing its
 * `usePlanEllipsisOptions`/`usePlanToOperationAction` hooks/modals verbatim.
 * "Create Recommendation" uses its own local modal state instead of the
 * shared `currentOpenModal`, since that hook is still shared with the
 * legacy (flag-off) menus. "Tag" is omitted — no tag chips exist yet.
 */
const PlanActionMenu: React.FC<PlanActionMenuProps> = ({
  plan,
  onPlanDeleted
}) => {
  const history = useHistory();
  const amplitude = useContext(AmplitudeContext);
  const [, dispatch] = (useContext(Context) as unknown) as [
    unknown,
    (action: unknown) => void
  ];
  const { user } = useAuth();
  const [isRecommendationModalOpen, setIsRecommendationModalOpen] = useState(
    false
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const { handleConvertPlanToOperationAction } = usePlanToOperationAction();
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  const plansApi = usePlanServiceApi(
    'plan',
    PLAN_DELETE_V2,
    PLAN_DELETE_V2_BY_ORG
  );

  const {
    dataSorted: propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    reloadData
  } = usePopulateMapData({
    fetchType: FETCH_TYPES.propertiesMap,
    cropSeasonsId: undefined
  }) as {
    dataSorted: unknown;
    fieldsAndAreasGeoJSONCollection: unknown;
    reloadData: () => void;
  };
  useEffect(() => {
    // Only needed by `handleConvertToRecommendation` below — deferred to
    // when that modal actually opens instead of fetching unconditionally
    // on every Plan selection (PlanPropertiesTab already loads the same
    // properties map data for the detail panel's Properties tab).
    if (isRecommendationModalOpen) reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecommendationModalOpen]);

  const {
    handleAction,
    currentOpenModal,
    setCurrentOpenModal,
    selectedOperationType,
    downloadingPDF
  } = usePlanEllipsisOptions(plan) as {
    handleAction: (action: string, id?: string) => void;
    currentOpenModal: string;
    setCurrentOpenModal: (modal: string) => void;
    selectedOperationType: OperationType;
    downloadingPDF: boolean;
  };

  const planToOperationModalContent = PLAN_TO_OPERATION_MODAL_CONTENT as Record<
    OperationType,
    { title: string; description: string }
  >;

  const closeModal = () => setCurrentOpenModal('');

  // No route navigation on delete — matches Applications/Work Orders'
  // inline-panel flow.
  const handleDelete = async () => {
    setIsDeleting(true);
    const { promise } = plansApi.delete(plan.id);
    await promise
      .then(() => {
        onPlanDeleted?.(plan.id);
      })
      .catch(catchCancel)
      .catch(() => planNotification('DELETE_ERROR'))
      .finally(() => setIsDeleting(false));
  };

  const handleDeleteClick = () => {
    showConfirmModal({
      title: 'Delete Plan?',
      content: (
        <>
          You will permanently lose{' '}
          <span className="font-semibold">all data</span> from this record.
        </>
      ),
      okText: 'Delete record',
      cancelText: 'Go back',
      onOk: handleDelete
    });
  };

  const navigateWithCropSeasonMismatchGuard = (
    navigate: () => void,
    actionType: 'edit' | 'duplicate'
  ) => {
    if (shouldShowPlanCropSeasonMismatch(plan, selectedCropSeasonIds)) {
      showCropSeasonMismatchModal(
        navigate,
        OperationType.PLAN,
        RecordSourceType.PLANS,
        { actionType }
      );
      return;
    }
    navigate();
  };

  const handleEdit = () => {
    // PR5/FR-017: routes to the wizard via `PlanFormRouteGuard`, not the
    // legacy `${paths.plans}/:id` + `selectedView` route-state pattern
    // `usePlanEllipsisOptions`'s own (now-unused-here) `edit()` still uses.
    navigateWithCropSeasonMismatchGuard(
      () => history.push(`${paths.plans}/${plan.id}/edit`),
      'edit'
    );
  };

  const handleDuplicate = () => {
    // PR5/FR-018: overrides `usePlanEllipsisOptions`'s own `duplicate()`,
    // which must keep pushing to the legacy route unchanged (spec SC-004)
    // since it's shared with the legacy (flag-off) menus.
    navigateWithCropSeasonMismatchGuard(
      () =>
        history.push(`${paths.plans}/create`, {
          flow: 'duplicate',
          sourcePlanId: plan.id
        }),
      'duplicate'
    );
  };

  const handleConvertToRecommendation = (
    selectedProducts: SelectedProducts
  ) => {
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.Plans.initiatePlanToRec,
      {
        'Product Count': selectedProducts.length,
        'Plan ID': plan.id,
        'Plan Name': plan.name
      }
    );

    const convertPlanToRec = mapPlanToRec(
      user,
      plan,
      selectedProducts,
      propertyLandingPageData,
      fieldsAndAreasGeoJSONCollection
    );

    dispatch({ type: 'SET_CONVERT_PLAN', payload: convertPlanToRec });
    setIsRecommendationModalOpen(false);
    history.push(`${paths.recommendations}/convert`);
  };

  const handleConvertPlanToOperation = (
    selectedProducts: SelectedProducts,
    conversionType: OperationType
  ) => {
    amplitude.sendEventToAmplitude(
      conversionType === OperationType.APPLICATION
        ? amplitude.events.epic.Plans.initiatePlanToApplication
        : amplitude.events.epic.Plans.initiatePlanToWorkOrder
    );
    const path =
      conversionType === OperationType.APPLICATION
        ? paths.applications
        : paths.workOrders;

    history.push(`${path}/create`, {
      plan,
      operationType: conversionType,
      flow: Flow.FROM_PLAN_TO_OPERATION,
      selectedProducts,
      returnPath: paths.plans
    });
  };

  const menuItems: MenuItem[] = [
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <DuplicateIcon style={MENU_ICON_STYLE} />,
      textColor: '#1F2937',
      hoverBgColor: '#F3F4F6',
      onClick: handleDuplicate
    },
    {
      key: 'download-pdf',
      label: 'Download PDF',
      icon: <PDFIcon style={MENU_ICON_STYLE} />,
      textColor: '#1F2937',
      hoverBgColor: '#F3F4F6',
      onClick: () => handleAction('downloadPDFEnglish', plan.id)
    },
    {
      key: 'create-recommendation',
      label: 'Create Recommendation',
      icon: <AddIcon style={MENU_ICON_STYLE} />,
      textColor: '#1F2937',
      hoverBgColor: '#F3F4F6',
      onClick: () => setIsRecommendationModalOpen(true)
    },
    {
      key: 'create-work-order',
      label: 'Create Work Order',
      icon: <AddIcon style={MENU_ICON_STYLE} />,
      textColor: '#1F2937',
      hoverBgColor: '#F3F4F6',
      onClick: () =>
        handleConvertPlanToOperationAction(
          plan,
          OperationType.WORK_ORDER,
          RecordSourceType.PLANS,
          () => handleAction('createWorkOrder', plan.id)
        )
    },
    {
      key: 'create-application',
      label: 'Create Application',
      icon: <AddIcon style={MENU_ICON_STYLE} />,
      textColor: '#1F2937',
      hoverBgColor: '#F3F4F6',
      onClick: () =>
        handleConvertPlanToOperationAction(
          plan,
          OperationType.APPLICATION,
          RecordSourceType.PLANS,
          () => handleAction('convertToApplication', plan.id)
        )
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlinedIcon style={DELETE_ICON_STYLE} />,
      textColor: '#D92D20',
      hoverBgColor: '#FEF3F2',
      onClick: handleDeleteClick
    }
  ];

  return (
    <div className="flex items-center gap-2">
      <EditButton onEdit={handleEdit} />
      <DropdownMenu
        items={menuItems}
        overlayClassName="plan-action-menu-dropdown"
      />

      <ConvertPlanModalV2
        title="Create Recommendation from Plan"
        description="Select the product(s) you want to include in your Recommendation"
        onSubmit={handleConvertToRecommendation}
        conversionType={OperationType.APPLICATION}
        open={isRecommendationModalOpen}
        onClose={() => setIsRecommendationModalOpen(false)}
        currentPlan={plan}
      />
      <ConvertPlanModalV2
        title={planToOperationModalContent[selectedOperationType]?.title}
        description={
          planToOperationModalContent[selectedOperationType]?.description
        }
        onSubmit={handleConvertPlanToOperation}
        conversionType={selectedOperationType}
        open={currentOpenModal === 'convertToOperation'}
        onClose={closeModal}
        currentPlan={plan}
      />
      <SpinnerModal open={downloadingPDF} message="Downloading PDF..." />
      <SpinnerModal open={isDeleting} message="Deleting Plan..." />
    </div>
  );
};

export default PlanActionMenu;
